import {
  type AdjustBody,
  type BillingAttention,
  type CancelBody,
  type CompleteBody,
  type Course,
  CSRF_HEADER,
  type Dashboard,
  type Discipline,
  type EnrollBody,
  type ErrorCode,
  type Group,
  type GroupBody,
  type GroupDetails,
  type GroupPatch,
  type ImportResult,
  type ImportStudentsBody,
  type Invite,
  type InviteBody,
  type JoinBody,
  type JoinInfo,
  type Lesson,
  type LessonCreateBody,
  type LessonDetails,
  type LessonMoveBody,
  type LoginBody,
  type LogoBody,
  type Me,
  type MoveEnrollmentBody,
  type PackageTemplate,
  type PackageTemplateBody,
  type PaymentBody,
  type SchoolPublic,
  type SchoolSettingsBody,
  type SellPackageBody,
  type SeriesChangeBody,
  type StudentCard,
  type Teacher,
  type UserBody,
  type UserListItem,
  type UserPatch,
  type UserStatusBody,
  type UserStatusResult,
} from '@aurea/contracts';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/v1${path}`, {
    method,
    credentials: 'same-origin',
    headers: {
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(method !== 'GET' ? { [CSRF_HEADER]: '1' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok)
    throw new ApiError(res.status, data?.code ?? 'INTERNAL', data?.message ?? res.statusText, data?.details);
  return data as T;
}

const qs = (o: Record<string, string | undefined>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const api = {
  schoolPublic: () => request<SchoolPublic>('GET', '/school/public'),
  saveSchoolSettings: (b: SchoolSettingsBody) => request<void>('PUT', '/school/settings', b),
  uploadLogo: (b: LogoBody) => request<void>('PUT', '/school/logo', b),
  deleteLogo: () => request<void>('DELETE', '/school/logo'),
  login: (b: LoginBody) => request<void>('POST', '/auth/login', b),
  logout: () => request<void>('POST', '/auth/logout'),
  me: () => request<Me>('GET', '/me'),
  calendarFeed: () => request<{ url: string }>('POST', '/me/calendar'),
  setupInfo: (token: string) => request<{ firstName: string; email: string }>('GET', `/auth/setup/${token}`),
  setup: (token: string, password: string) => request<void>('POST', `/auth/setup/${token}`, { password }),

  dashboard: () => request<Dashboard>('GET', '/dashboard'),
  lessons: (from: string, to: string, teacherId?: string) =>
    request<Lesson[]>('GET', `/lessons${qs({ from, to, teacherId })}`),
  lesson: (id: string) => request<LessonDetails>('GET', `/lessons/${id}`),
  moveLesson: (id: string, b: LessonMoveBody) => request<Lesson>('PATCH', `/lessons/${id}`, b),
  changeSeries: (id: string, b: SeriesChangeBody) =>
    request<{ updated: number }>('POST', `/lessons/${id}/series`, b),
  completeLesson: (id: string, b: CompleteBody) =>
    request<LessonDetails & { charged: number; uncharged: number }>('POST', `/lessons/${id}/complete`, b),
  teachers: () => request<Teacher[]>('GET', '/teachers'),
  createLesson: (b: LessonCreateBody) =>
    request<{ created: number; seriesId: string | null; firstLessonId: string | null }>(
      'POST',
      '/lessons',
      b,
    ),
  cancelLesson: (id: string, b: CancelBody) =>
    request<{ cancelled: number }>('POST', `/lessons/${id}/cancel`, b),

  users: (q: { role?: string; q?: string; status?: string }) =>
    request<UserListItem[]>('GET', `/users${qs(q)}`),
  createUser: (b: UserBody) => request<{ id: string; setupUrl: string }>('POST', '/users', b),
  importStudents: (b: ImportStudentsBody) => request<ImportResult>('POST', '/users/import', b),
  updateUser: (id: string, b: UserPatch) => request<void>('PATCH', `/users/${id}`, b),
  setUserStatus: (id: string, b: UserStatusBody) =>
    request<UserStatusResult>('POST', `/users/${id}/status`, b),
  userLink: (id: string) => request<{ url: string }>('POST', `/users/${id}/link`),

  disciplines: () => request<Discipline[]>('GET', '/disciplines'),
  createDiscipline: (b: { name: string; color: string }) =>
    request<{ id: string }>('POST', '/disciplines', b),
  courses: () => request<Course[]>('GET', '/courses'),
  createCourse: (b: {
    disciplineId: string;
    name: string;
    format: string;
    defaultDurationMin: number;
    teacherIds: string[];
  }) => request<{ id: string }>('POST', '/courses', b),
  groups: () => request<Group[]>('GET', '/groups'),
  group: (id: string) => request<GroupDetails>('GET', `/groups/${id}`),
  createGroup: (b: GroupBody) => request<{ id: string }>('POST', '/groups', b),
  updateGroup: (id: string, b: GroupPatch) => request<void>('PATCH', `/groups/${id}`, b),
  archiveGroup: (id: string) => request<void>('POST', `/groups/${id}/archive`),
  enroll: (b: EnrollBody) => request<{ id: string }>('POST', '/enrollments', b),
  moveEnrollment: (id: string, b: MoveEnrollmentBody) => request<void>('PATCH', `/enrollments/${id}`, b),
  unenroll: (id: string) => request<void>('DELETE', `/enrollments/${id}`),

  packageTemplates: () => request<PackageTemplate[]>('GET', '/package-templates'),
  createPackageTemplate: (b: PackageTemplateBody) => request<{ id: string }>('POST', '/package-templates', b),
  archivePackageTemplate: (id: string) => request<void>('POST', `/package-templates/${id}/archive`),
  student: (id: string) => request<StudentCard>('GET', `/students/${id}`),
  sellPackage: (studentId: string, b: SellPackageBody) =>
    request<{ id: string; coveredDebt: number }>('POST', `/students/${studentId}/packages`, b),
  adjustPackage: (id: string, b: AdjustBody) => request<void>('POST', `/student-packages/${id}/adjust`, b),
  addPayment: (studentId: string, b: PaymentBody) =>
    request<{ id: string }>('POST', `/students/${studentId}/payments`, b),
  billingAttention: () => request<BillingAttention>('GET', '/billing/attention'),

  invites: () => request<Invite[]>('GET', '/invites'),
  createInvite: (b: InviteBody) => request<Invite & { url: string }>('POST', '/invites', b),
  revokeInvite: (id: string) => request<void>('POST', `/invites/${id}/revoke`),
  joinInfo: (token: string) => request<JoinInfo>('GET', `/join/${token}`),
  join: (token: string, b: JoinBody) => request<void>('POST', `/join/${token}`, b),
};
